import Tooltip from 'core/components/dialogs/Tooltip'
import Icon from 'core/components/icons/Icon'
import Select from 'core/components/inputs/Select'
import { useMemo } from 'react'
import { TaxReport } from 'types/entity'
import { tableCellFormat } from 'utils/formatter'
import { toCurrency } from 'utils/Functions'

interface Props {
  year: number
  setYear: (year: number) => void
  summaryData: TaxReport['summary'] | undefined
  isLoading: boolean
}

const AnnualSummary = ({
  year,
  setYear,
  summaryData,
  isLoading = true
}: Props) => {
  const currentYear = new Date().getFullYear()
  const yearsOptions = useMemo(() => {
    const years = []
    for (let i = currentYear; i >= 2025; i--) {
      years.push(i)
    }
    return years
  }, [currentYear])

  const {
    approved_mileage,
    payments_received,
    payments_received_while_non_compliant,
    subject_to_tax
  } = summaryData ?? {
    approvedMileage: 0,
    paymentsReceived: 0,
    paymentsReceivedNonCompliant: 0,
    subjectToTax: 0
  }

  return (
    <div className="card h-full flex flex-col">
      <div className="flex justify-between mb-5">
        <h2>Annual Tax Summary Totals</h2>
        <Select
          options={yearsOptions}
          value={year}
          onChange={(val) => setYear(val as number)}
        />
      </div>
      <div className="flex flex-col md:grid md:grid-cols-4 gap-6 justify-between mt-auto mb-5">
        <div>
          {isLoading ? (
            <div className="skeleton w-32 h-7 mb-2" />
          ) : (
            <h3>{tableCellFormat(approved_mileage ?? 0, 'miles')}</h3>
          )}

          <div className="flex gap-2">
            <p>Approved mileage</p>
            <Tooltip
              dark
              position="bottom-middle"
              content="Total mileage approved during the tax year"
            >
              <Icon name="help-outline" className="mt-1" noWrapper />
            </Tooltip>
          </div>
        </div>
        <div>
          {isLoading ? (
            <div className="skeleton w-32 h-7 mb-2" />
          ) : (
            <h3>{toCurrency(Number(payments_received ?? 0))}</h3>
          )}
          <div className="flex gap-2">
            <p>Payments received</p>
            <Tooltip
              dark
              position="bottom-middle"
              content="Value of total mileage approved during the tax year"
            >
              <Icon name="help-outline" className="mt-1" noWrapper />
            </Tooltip>
          </div>
        </div>
        <div>
          {isLoading ? (
            <div className="skeleton w-32 h-7 mb-2" />
          ) : (
            <h3>
              {toCurrency(Number(payments_received_while_non_compliant ?? 0))}
            </h3>
          )}
          <div className="flex gap-2">
            <p>Payments received while non-compliant</p>
            <Tooltip
              dark
              position="bottom-middle"
              content="Value of approved mileage in months with non-compliance"
            >
              <Icon name="help-outline" className="mt-1" noWrapper />
            </Tooltip>
          </div>
        </div>
        <div>
          {isLoading ? (
            <div className="skeleton w-32 h-7 mb-2" />
          ) : (
            <h3>{toCurrency(Number(subject_to_tax ?? 0))}</h3>
          )}
          <div className="flex gap-2">
            <p>Subject to tax</p>
            <Tooltip
              dark
              position="bottom-middle"
              content="The amount your employer will account for as taxable ordinary income"
            >
              <Icon name="help-outline" className="mt-1" noWrapper />
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AnnualSummary
